import Page, { Meta } from "@layouts/page";
import { PageWithLayout } from "@types";
import { getLayout as getDefaultLayout } from "@layouts/default-layout";
import { GetServerSideProps } from "next";
import { setContext } from "@utils/Cookies";
import newExploreApi from "@api/new-explore";
import { EXPLORE_SLUG } from "types/api/explore";
import { exploreMethodBySlug } from "types/api/new-explore";
import NotFound from "./404";
import { useRouter } from "next/router";
import NewExplore from "@templates/new-explore";
import { adaptPayload } from "@utils/adaptedpayloadExplore";
import { fetchExploreData } from "lib/ssr/explore";
import { useTranslations } from "next-intl";
import { useExploreStoreSync } from "@templates/new-explore/hooks/useExploreStoreSync";
import { ExploreInputDT } from "@templates/new-explore/hooks/types";
import useRequestImmutable from "@shared/hooks/useRequestImmutable";
import { setNotFoundResponse } from "lib/ssr/not-found";

const ExplorePage: PageWithLayout<{ is404?: boolean }> = ({ is404 }) => {
  useExploreStoreSync();

  const { query } = useRouter();
  const slug =
    typeof query.slug === "string"
      ? (query.slug as EXPLORE_SLUG)
      : EXPLORE_SLUG.COURSE;

  const t = useTranslations("meta");

  const { data: exploreData, error } = useRequestImmutable(
    newExploreApi[exploreMethodBySlug[slug]](
      adaptPayload(query)
    ) as ExploreInputDT
  );

  if (is404 || error?.error?.code === 404) return <NotFound />;

  const title = exploreData?.seo?.title || t("title");
  const description = exploreData?.seo?.description || t("desc");
  const keywords = exploreData?.seo?.keywords;
  const meta: Meta = {
    title,
    description,
    ...(keywords?.length && { keywords }),
    image:
      "https://cdn.faradars.org/wp-content/uploads/2022/11/23/explorer.png",
  };
  return (
    <Page meta={meta}>
      <NewExplore />
    </Page>
  );
};

ExplorePage.translationFile = "new-explore";
ExplorePage.getLayout = (page, props) =>
  getDefaultLayout(page, { ...props }, {});

export const getServerSideProps: GetServerSideProps = async (ctx) => {
  const { query, res, req } = ctx;
  // eslint-disable-next-line no-unused-vars
  const { logger, ssr, count, ...filtersQuery } = query;
  setContext(ctx);

  const theSsrHeader = req.headers["x-ssr-activate"];
  const ssrActivated =
    String(theSsrHeader).toLowerCase() !== "false" || ssr === "1";

  const shouldLog = Boolean(logger);

  if (!ssrActivated) {
    if (shouldLog) console.log("Explore logger: No Request sent to server");
    return { props: { should_ssr: false, query_params: filtersQuery } };
  }

  try {
    const fallback = await fetchExploreData(filtersQuery);
    if (shouldLog)
      console.log("SSR request sent", { request_query: filtersQuery });
    return {
      props: {
        fallback,
        should_ssr: true,
        query_params: filtersQuery,
      },
    };
  } catch (err: any) {
    if (shouldLog) console.log("Explore logger: Error occured", { error: err });
    if (err?.error?.code === 404) setNotFoundResponse(res);

    return {
      props: {
        should_ssr: false,
        query_params: filtersQuery,
        is404: err?.error?.code === 404,
      },
    };
  }
};

export default ExplorePage;
